/**
 * The family's details column — v2's `FamilyAuthoringDetailsPanel`: its name and
 * description, then Parameters · Run Summary.
 *
 * Parameters are the family's own controls (number with a slider, text, a
 * switch), applied with one button that re-runs the family's code in Revit
 * against the new values. That run is not a chat turn and never enters the
 * conversation (v2: "keep parameter runs out of chat"). A contract the agent left
 * invalid, or a run that failed, shows its error with a button that writes a
 * repair request into the chat for the user to send.
 */
import { useEffect, useRef, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { ExecutionDisplayView, isDisplayPayload } from "../components/skills/ExecutionDisplayView";
import { LoaderIcon, PlayIcon, WrenchIcon } from "../components/skills/icons";
import type { FamilyParameter } from "./types";
import type { FamilyBuilderController } from "./useFamilyBuilder";

type DetailsTab = "parameters" | "summary";

export function FamilyBuilderDetails({
  controller,
  onPasteIntoChat,
}: {
  controller: FamilyBuilderController;
  onPasteIntoChat: (message: string) => void;
}) {
  const { t } = useLocale();
  const view = controller.view;
  const hasParameters = controller.parameters.length > 0;
  const contractError =
    view && view.parameterContract?.status !== "valid"
      ? view.parameterContract?.errors.join("\n") || "Family parameter contract status is unavailable."
      : null;
  const hasParameterPanel = hasParameters || contractError !== null;
  const [tab, setTab] = useState<DetailsTab>(hasParameterPanel ? "parameters" : "summary");
  const hadParameterPanel = useRef(hasParameterPanel);

  // A family that gains parameters opens on them; one that loses them falls back to the summary.
  useEffect(() => {
    if (!hadParameterPanel.current && hasParameterPanel) setTab("parameters");
    if (hadParameterPanel.current && !hasParameterPanel) setTab("summary");
    hadParameterPanel.current = hasParameterPanel;
  }, [hasParameterPanel]);

  const apply = async () => {
    const ok = await controller.applyParameters();
    if (!ok) setTab("summary");
  };

  const disabled = controller.isBusy || !controller.isRfaReady;
  // The latest recorded run (v2's latestOnly history panel); this visit's Apply answer until it loads.
  const display = controller.latestRun?.display ?? controller.lastExecution?.display;
  const runMessage = controller.latestRun
    ? (controller.latestRun.success ? null : controller.latestRun.errorMessage || null)
    : controller.lastExecution?.message ?? null;

  return (
    <section className="family-builder-details">
      <div className="family-builder-desc">
        <h3>{view?.artifact.displayName || t("familyBuilder.title")}</h3>
        <p>{view?.artifact.description || t("familyBuilder.noDescription")}</p>
        {view ? (
          <p className="family-builder-desc-meta">
            v{view.version}
            {view.revitVersion ? ` · Revit ${view.revitVersion}` : ""}
          </p>
        ) : null}
      </div>

      <div className="skill-tabs" role="tablist">
        {hasParameterPanel ? (
          <button
            type="button"
            role="tab"
            aria-selected={tab === "parameters"}
            className={`skill-tab${tab === "parameters" ? " is-active" : ""}`}
            onClick={() => setTab("parameters")}
          >
            {t("familyBuilder.parameters")}
            {hasParameters ? <span className="family-builder-count">{controller.parameters.length}</span> : null}
          </button>
        ) : null}
        <button
          type="button"
          role="tab"
          aria-selected={tab === "summary"}
          className={`skill-tab${tab === "summary" ? " is-active" : ""}`}
          onClick={() => setTab("summary")}
        >
          {t("familyBuilder.summary")}
        </button>
      </div>

      {tab === "parameters" && hasParameterPanel ? (
        contractError !== null ? (
          <div className="family-builder-tab-body">
            <RepairableError
              message={t("familyBuilder.parameterContractInvalid")}
              onRepair={() => onPasteIntoChat(`${t("familyBuilder.fixParameterContractPrompt")}\n\n${contractError}`)}
            />
          </div>
        ) : (
          <>
            <div className="family-builder-tab-body">
              <div className="family-builder-params">
                {controller.parameters.map((parameter) => (
                  <ParameterControl
                    key={parameter.key}
                    parameter={parameter}
                    disabled={disabled}
                    onChange={(value) => controller.updateParameter(parameter.key, value)}
                  />
                ))}
              </div>
            </div>
            <div className="family-builder-apply">
              <button
                type="button"
                className="settings-btn family-builder-apply-btn"
                disabled={!controller.hasParameterChanges || disabled}
                onClick={() => void apply()}
              >
                {controller.isApplyingParameters ? <LoaderIcon size={14} /> : <PlayIcon size={14} />}
                {t("familyBuilder.applyParameters")}
              </button>
            </div>
          </>
        )
      ) : (
        <div className="family-builder-tab-body">
          {controller.parameterExecutionError ? (
            <RepairableError
              message={controller.parameterExecutionError}
              onRepair={() =>
                onPasteIntoChat(`${t("familyBuilder.fixExecutionErrorPrompt")}\n\n${controller.parameterExecutionError}`)
              }
            />
          ) : display && isDisplayPayload(display) ? (
            <ExecutionDisplayView display={display} />
          ) : runMessage ? (
            <p className="family-builder-summary-text">{runMessage}</p>
          ) : (
            <p className="skills-hint">{t("familyBuilder.summaryEmpty")}</p>
          )}
        </div>
      )}
    </section>
  );
}

function ParameterControl({
  parameter,
  disabled,
  onChange,
}: {
  parameter: FamilyParameter;
  disabled: boolean;
  onChange: (value: FamilyParameter["value"]) => void;
}) {
  const id = `family-parameter-${parameter.key}`;
  if (parameter.type === "boolean") {
    return (
      <div className="family-builder-param is-inline">
        <label htmlFor={id} className="family-builder-param-label">
          {parameter.label}
        </label>
        <input
          id={id}
          type="checkbox"
          className="family-builder-switch"
          role="switch"
          checked={Boolean(parameter.value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </div>
    );
  }
  if (parameter.type === "string") {
    return (
      <div className="family-builder-param">
        <label htmlFor={id} className="family-builder-param-label">
          {parameter.label}
        </label>
        <input id={id} className="settings-input" value={String(parameter.value)} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  return <NumericControl parameter={parameter} disabled={disabled} onChange={onChange} />;
}

/**
 * A number with a slider — v2's control, including its clamping: a typed value
 * outside the range is clamped when the field is left, never sent as typed.
 */
function NumericControl({
  parameter,
  disabled,
  onChange,
}: {
  parameter: FamilyParameter;
  disabled: boolean;
  onChange: (value: FamilyParameter["value"]) => void;
}) {
  const id = `family-parameter-${parameter.key}`;
  const value = Number(parameter.value);
  const minimum = Number(parameter.minimum);
  const maximum = Number(parameter.maximum);
  const step = Number(parameter.step);
  const [draft, setDraft] = useState(String(value));
  const clamp = (next: number) => Math.min(maximum, Math.max(minimum, next));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const next = Number(draft);
    if (draft.trim() === "" || !Number.isFinite(next)) {
      setDraft(String(value));
      return;
    }
    const committed = clamp(next);
    setDraft(String(committed));
    onChange(committed);
  };

  return (
    <div className="family-builder-param">
      <div className="family-builder-param-row">
        <label htmlFor={id} className="family-builder-param-label">
          {parameter.label}
        </label>
        <span className="family-builder-param-value">
          <input
            id={id}
            type="number"
            className="settings-input"
            min={minimum}
            max={maximum}
            step={step}
            value={draft}
            disabled={disabled}
            onChange={(e) => {
              const next = e.target.value;
              setDraft(next);
              if (next.trim() === "") return;
              const parsed = Number(next);
              if (Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum) onChange(parsed);
            }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
          />
          {parameter.unit ? <span className="family-builder-param-unit">{parameter.unit}</span> : null}
        </span>
      </div>
      <input
        type="range"
        className="family-builder-slider"
        aria-label={parameter.label}
        min={minimum}
        max={maximum}
        step={step}
        value={clamp(value)}
        disabled={disabled}
        onChange={(e) => {
          const next = Number(e.target.value);
          setDraft(String(next));
          onChange(next);
        }}
      />
    </div>
  );
}

function RepairableError({ message, onRepair }: { message: string; onRepair: () => void }) {
  const { t } = useLocale();
  return (
    <div className="family-builder-repair">
      <p>{message}</p>
      <button
        type="button"
        className="settings-btn secondary is-small"
        onClick={onRepair}
        title={t("familyBuilder.fixExecutionError")}
        aria-label={t("familyBuilder.fixExecutionError")}
      >
        <WrenchIcon size={13} />
        <span className="btn-label">{t("familyBuilder.fixExecutionError")}</span>
      </button>
    </div>
  );
}
